// #543: a lazy page whose code file cannot be loaded used to unmount the whole
// app, leaving an empty #root on /login and /privacy. The file goes missing
// when a deploy replaces it: a chunk requested mid-switchover, or by a tab
// opened before the deploy, gets Cloudflare Pages' index.html fallback instead
// (200 text/html). fragranceos.io lets browsers keep that answer for 4 hours,
// so a plain reload reuses it. Recovery removes the cached HTML, then reloads
// once; a second failure inside the window shows a Reload screen instead.

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload|Expected a JavaScript.* module script/i

export const CHUNK_RELOAD_KEY = 'fos_chunk_reload_at'
export const CHUNK_RELOAD_WINDOW_MS = 30_000

export function isChunkLoadError(error) {
  return CHUNK_ERROR.test(`${error?.message ?? error ?? ''}`)
}

// One automatic reload per window, so a file that is really gone cannot loop.
export function claimChunkReload(storage, now = Date.now()) {
  try {
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY) || 0)
    if (last && now - last < CHUNK_RELOAD_WINDOW_MS) return false
    storage.setItem(CHUNK_RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

const isHtml = res => /text\/html/i.test(res?.headers?.get?.('content-type') || '')

function sameOriginScriptAssets(urls, origin) {
  const found = new Set()
  for (const raw of urls) {
    try {
      const url = new URL(raw, origin)
      if (url.origin === origin && url.pathname.startsWith('/assets/') && url.pathname.endsWith('.js')) found.add(url.href)
    } catch {
      // Not a URL; ignore.
    }
  }
  return [...found]
}

// Chromium names the failed file in the error; Safari does not, so resource
// timing supplies every script asset this page requested.
export function candidateAssetUrls(error, resourceNames, origin) {
  const fromError = sameOriginScriptAssets(`${error?.message ?? ''}`.match(/https?:\/\/[^\s'"]+/g) || [], origin)
  return fromError.length ? fromError : sameOriginScriptAssets(resourceNames, origin)
}

export async function clearPoisonedAssets({ urls, cachesImpl, fetchImpl }) {
  // 1) Service-worker caches: drop HTML stored under an asset name. A worker
  //    from before this fix serves those entries cache-first until it retires.
  const purged = []
  if (cachesImpl) {
    for (const name of await cachesImpl.keys()) {
      if (!name.startsWith('fragrance-os-')) continue
      const cache = await cachesImpl.open(name)
      for (const request of await cache.keys()) {
        if (!new URL(request.url).pathname.startsWith('/assets/')) continue
        if (isHtml(await cache.match(request)) && await cache.delete(request)) purged.push(request.url)
      }
    }
  }
  // 2) Browser HTTP cache: only a reload fetch replaces a still-fresh entry. It
  //    must follow step 1, or an older worker answers it from its own cache.
  //    Healthy files the current worker holds are served without a network trip.
  await Promise.all(urls.map(url => fetchImpl(url, { cache: 'reload' }).catch(() => null)))
  return purged
}

export async function repairAssetCaches(error, win = window) {
  const names = win.performance?.getEntriesByType?.('resource')?.map(entry => entry.name) || []
  const urls = candidateAssetUrls(error, names, win.location.origin)
  try {
    await clearPoisonedAssets({ urls, cachesImpl: win.caches, fetchImpl: win.fetch.bind(win) })
  } catch (repairError) {
    console.warn('Asset cache repair failed', repairError)
  }
}

// Repairs on every failure, so the next reload works even when this one does
// not reload itself. Returns true when a reload is under way.
export async function recoverFromChunkError(error, win = window) {
  await repairAssetCaches(error, win)
  let storage = null
  try { storage = win.sessionStorage } catch { return false }
  if (!storage || !claimChunkReload(storage)) return false
  win.location.reload()
  return true
}
